import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Baby } from 'lucide-react';
import type { ToothChartEntry } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { visibleInterval } from '@/lib/polling';
import { cn, dateOnly, dateTime, humanize, timeOnly, todayIso } from '@/lib/utils';
import { DataTable, Dialog, FilterBar, FilterField, PageHeader, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Field, Input, Select, StatusBadge, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';

// ── Labour & delivery (SRS 6.8) ──────────────────────────────────────────────

interface Delivery {
  id: string;
  mode: string;
  outcome: string;
  gravida: number | null;
  para: number | null;
  gestationWeeks: number | null;
  onsetAt: string | null;
  deliveredAt: string | null;
  bloodLossMl: number | null;
  complications: string | null;
  partograph: Record<string, unknown>[];
  patient: { id: string; mrn: string; fullName: string };
  newborns: {
    id: string;
    sex: string;
    birthWeightG: number | null;
    apgar1: number | null;
    apgar5: number | null;
    patientId: string | null;
    bornAt: string;
  }[];
}

export function MaternityPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [open, setOpen] = useState<string | null>(() => params.get('delivery'));

  const deliveries = useQuery({
    queryKey: ['deliveries'],
    queryFn: () => get<Delivery[]>('/maternity/deliveries?limit=100'),
    refetchInterval: visibleInterval(60_000),
  });

  const rows = deliveries.data ?? [];
  const inLabour = rows.filter((row) => !row.deliveredAt);

  return (
    <>
      <PageHeader
        title={tr('ipd', 'sp_labour_title')}
        description={tr('ipd', 'sp_labour_description')}
        actions={<Button onClick={() => navigate('/maternity/deliveries/new')}>{tr('ipd', 'sp_open_labour_record')}</Button>}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile label={tr('ipd', 'sp_in_labour')} value={inLabour.length} tone={inLabour.length ? 'warning' : 'default'} />
        <StatTile label={tr('ipd', 'sp_delivered')} value={rows.length - inLabour.length} />
        <StatTile label={tr('ipd', 'sp_babies')} value={rows.reduce((sum, row) => sum + row.newborns.length, 0)} tone="success" />
      </div>

      <DataTable
        rows={rows}
        loading={deliveries.isLoading} refreshing={deliveries.isFetching}
        error={deliveries.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setOpen(row.id)}
        empty={tr('ipd', 'sp_no_labour_records')}
        columns={[
          {
            header: tr('ipd', 'sp_mother'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
              </div>
            ),
          },
          {
            header: tr('ipd', 'sp_gravida_para'),
            sort: (row) => row.gravida,
            cell: (row) => (row.gravida ? `G${row.gravida}${row.para !== null ? ` P${row.para}` : ''}` : '—'),
          },
          {
            header: tr('ipd', 'sp_gestation'),
            sort: (row) => row.gestationWeeks,
            cell: (row) => (row.gestationWeeks ? tr('ipd', 'sp_weeks_short', { weeks: row.gestationWeeks }) : '—'),
          },
          { header: tr('ipd', 'sp_onset'), sort: (row) => row.onsetAt, cell: (row) => (row.onsetAt ? dateTime(row.onsetAt) : '—') },
          {
            header: tr('ipd', 'sp_delivered'),
            sort: (row) => row.deliveredAt,
            cell: (row) => (row.deliveredAt ? dateTime(row.deliveredAt) : <Badge variant="warning">{tr('ipd', 'sp_in_labour')}</Badge>),
          },
          {
            header: tr('ipd', 'sp_mode'),
            sort: (row) => (row.deliveredAt ? row.mode : null),
            cell: (row) => (row.deliveredAt ? humanize(row.mode, 'delivery_mode') : '—'),
          },
          {
            header: tr('ipd', 'sp_babies'),
            sort: (row) => row.newborns.length,
            cell: (row) =>
              row.newborns.length ? (
                <span className="inline-flex items-center gap-1">
                  <Baby className="h-4 w-4" /> {row.newborns.length}
                </span>
              ) : (
                '—'
              ),
          },
        ]}
      />

      {open !== null && (
        <DeliveryDialog deliveryId={open} onClose={() => setOpen(null)} />
      )}
    </>
  );
}


function DeliveryDialog({ deliveryId, onClose }: { deliveryId: string | null; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [entry, setEntry] = useState({ cervixCm: '', contractionsPer10Min: '', foetalHeartRate: '', liquor: 'clear' });

  const delivery = useQuery({
    queryKey: ['delivery', deliveryId],
    queryFn: () => get<Delivery>(`/maternity/deliveries/${deliveryId}`),
    enabled: Boolean(deliveryId),
  });

  const chart = useMutation({
    mutationFn: () =>
      post(`/maternity/deliveries/${deliveryId}/partograph`, {
        cervixCm: entry.cervixCm ? Number(entry.cervixCm) : undefined,
        contractionsPer10Min: entry.contractionsPer10Min ? Number(entry.contractionsPer10Min) : undefined,
        foetalHeartRate: entry.foetalHeartRate ? Number(entry.foetalHeartRate) : undefined,
        liquor: entry.liquor,
      }),
    onSuccess: () => {
      toast.success(tr('ipd', 'sp_observation_recorded'));
      void queryClient.invalidateQueries({ queryKey: ['delivery', deliveryId] });
      setEntry({ cervixCm: '', contractionsPer10Min: '', foetalHeartRate: '', liquor: 'clear' });
    },
    onError: (error) => toast.error(tr('ipd', 'sp_could_not_record'), describeError(error)),
  });

  const register = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => post(`/maternity/newborns/${id}/register`, { name }),
    onSuccess: () => {
      toast.success(tr('ipd', 'sp_baby_registered'), tr('ipd', 'sp_baby_has_mrn'));
      void queryClient.invalidateQueries({ queryKey: ['delivery', deliveryId] });
    },
    onError: (error) => toast.error(tr('ipd', 'sp_could_not_register_baby'), describeError(error)),
  });

  const d = delivery.data;

  return (
    <>
      <Dialog
        open={Boolean(deliveryId)}
        onClose={onClose}
        wide
        title={d ? d.patient.fullName : tr('ipd', 'sp_labour_record')}
        description={d ? `${d.patient.mrn}${d.gestationWeeks ? ` · ${tr('ipd', 'sp_x_weeks', { weeks: d.gestationWeeks })}` : ''}` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {tr('ipd', 'close')}
            </Button>
            {d && !d.deliveredAt && (
              <Button onClick={() => navigate(`/maternity/deliveries/${d.id}/complete?returnTo=${encodeURIComponent(`/maternity?delivery=${d.id}`)}`)}>
                {tr('ipd', 'sp_record_delivery')}
              </Button>
            )}
          </>
        }
      >
        {d && (
          <div className="space-y-4">
            {!d.deliveredAt && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{tr('ipd', 'sp_new_observation')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label={tr('ipd', 'sp_cervix_cm')}>
                      <Input
                        type="number"
                        min={0}
                        max={10}
                        value={entry.cervixCm}
                        onChange={(e) => setEntry({ ...entry, cervixCm: e.target.value })}
                      />
                    </Field>
                    <Field label={tr('ipd', 'sp_contractions_per_10')}>
                      <Input
                        type="number"
                        min={0}
                        max={10}
                        value={entry.contractionsPer10Min}
                        onChange={(e) => setEntry({ ...entry, contractionsPer10Min: e.target.value })}
                      />
                    </Field>
                    <Field label={tr('ipd', 'sp_foetal_heart')}>
                      <Input
                        type="number"
                        min={40}
                        max={240}
                        value={entry.foetalHeartRate}
                        onChange={(e) => setEntry({ ...entry, foetalHeartRate: e.target.value })}
                      />
                    </Field>
                    <Field label={tr('ipd', 'sp_liquor')} className="sm:col-span-2">
                      <ChoiceButtons<string>
                        layout="chips"
                        value={entry.liquor}
                        onChange={(value) => setEntry({ ...entry, liquor: value })}
                        options={['intact', 'clear', 'meconium', 'blood_stained', 'absent'].map((l) => ({ value: l, label: humanize(l, 'liquor') }))}
                      />
                    </Field>
                    <div className="flex items-end">
                      <Button className="w-full" onClick={() => chart.mutate()} loading={chart.isPending}>
                        {tr('ipd', 'record')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <h3 className="mb-2 text-sm font-semibold">{tr('ipd', 'sp_partograph')}</h3>
              <DataTable
                dense
                rows={d.partograph as unknown as Record<string, string | number>[]}
                rowKey={(row) => String(row.at)}
                empty={tr('ipd', 'sp_no_observations')}
                columns={[
                  { header: tr('ipd', 'sp_at'), cell: (row) => timeOnly(String(row.at)) },
                  { header: tr('ipd', 'sp_cervix'), align: 'right', cell: (row) => (row.cervixCm ? `${row.cervixCm} cm` : '—') },
                  { header: tr('ipd', 'sp_contractions'), align: 'right', cell: (row) => row.contractionsPer10Min ?? '—' },
                  {
                    header: tr('ipd', 'sp_foetal_heart'),
                    align: 'right',
                    cell: (row) => {
                      const rate = Number(row.foetalHeartRate);
                      if (!rate) return '—';
                      // Outside 110–160 is the range a midwife acts on.
                      return (
                        <span className={cn((rate < 110 || rate > 160) && 'font-semibold text-destructive')}>{rate}</span>
                      );
                    },
                  },
                  { header: tr('ipd', 'sp_liquor'), cell: (row) => (row.liquor ? humanize(String(row.liquor), 'liquor') : '—') },
                ]}
              />
            </div>

            {d.newborns.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold">{tr('ipd', 'sp_newborns')}</h3>
                <DataTable
                  dense
                  rows={d.newborns}
                  rowKey={(row) => row.id}
                  columns={[
                    { header: tr('ipd', 'sp_born'), cell: (row) => dateTime(row.bornAt) },
                    { header: tr('ipd', 'sp_sex'), cell: (row) => humanize(row.sex, 'sex') },
                    { header: tr('ipd', 'sp_weight'), align: 'right', cell: (row) => (row.birthWeightG ? `${row.birthWeightG} g` : '—') },
                    {
                      header: tr('ipd', 'sp_apgar'),
                      align: 'right',
                      cell: (row) => `${row.apgar1 ?? '—'} / ${row.apgar5 ?? '—'}`,
                    },
                    {
                      header: '',
                      align: 'right',
                      cell: (row) =>
                        row.patientId ? (
                          <Badge variant="success">{tr('ipd', 'sp_registered')}</Badge>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => register.mutate({ id: row.id, name: `Baby of ${d.patient.fullName}` })}
                          >
                            {tr('ipd', 'sp_give_mrn')}
                          </Button>
                        ),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}


// ── Dialysis, chemotherapy and physiotherapy (SRS 6.8) ───────────────────────

interface Session {
  id: string;
  number: string;
  unit: string;
  cycleNo: number;
  protocol: string | null;
  status: string;
  scheduledAt: string;
  startedAt: string | null;
  endedAt: string | null;
  durationMinutes: number;
  preWeightKg: number | null;
  postWeightKg: number | null;
  invoiceId: string | null;
  patient: { id: string; mrn: string; fullName: string };
  station: { id: string; code: string; name: string } | null;
}

export function TherapyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [unit, setUnit] = useState(() => params.get('unit') ?? 'dialysis');
  const [date, setDate] = useState(() => params.get('date') ?? todayIso());
  const [finishing, setFinishing] = useState<Session | null>(null);

  const stations = useQuery({
    queryKey: ['stations', unit],
    queryFn: () => get<{ id: string; code: string; name: string }[]>(`/therapy/stations${qs({ unit })}`),
  });

  const sessions = useQuery({
    queryKey: ['therapy-sessions', unit, date],
    queryFn: () => get<Session[]>(`/therapy/sessions${qs({ unit, date })}`),
    refetchInterval: visibleInterval(60_000),
  });

  const start = useMutation({
    mutationFn: (session: Session) => post(`/therapy/sessions/${session.id}/start`, {}),
    onSuccess: () => {
      toast.success(tr('ipd', 'sp_session_started'));
      void queryClient.invalidateQueries({ queryKey: ['therapy-sessions'] });
    },
    onError: (error) => toast.error(tr('ipd', 'sp_could_not_start_session'), describeError(error)),
  });

  const rows = sessions.data ?? [];

  return (
    <>
      <PageHeader
        title={tr('ipd', 'sp_therapy_title')}
        description={tr('ipd', 'sp_therapy_description')}
        actions={<Button onClick={() => navigate(`/therapy/sessions/new?unit=${unit}&date=${date}`)}>{tr('ipd', 'sp_book_session')}</Button>}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <StatTile label={tr('ipd', 'sp_stations')} value={stations.data?.length ?? 0} />
        <StatTile label={tr('ipd', 'sp_booked_today')} value={rows.length} />
        <StatTile label={tr('ipd', 'sp_running')} value={rows.filter((r) => r.status === 'in_progress').length} tone="warning" />
        <StatTile label={tr('ipd', 'sp_completed')} value={rows.filter((r) => r.status === 'completed').length} tone="success" />
      </div>

      <SearchFilterBar
        filters={
          <>
            <FilterField label={tr('ipd', 'sp_unit')}>
              <Select value={unit} onChange={(e) => setUnit(e.target.value)} className="w-44">
                {['dialysis', 'chemotherapy', 'physiotherapy', 'day_care'].map((u) => (
                  <option key={u} value={u}>
                    {humanize(u, 'therapy_unit')}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label={tr('ipd', 'sp_date')}>
              <DatePicker value={date} onChange={setDate} className="w-40" />
            </FilterField>
          </>
        }
        onReset={
          unit !== 'dialysis' || date !== todayIso()
            ? () => {
                setUnit('dialysis');
                setDate(todayIso());
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={sessions.isLoading} refreshing={sessions.isFetching}
        error={sessions.error}
        rowKey={(row) => row.id}
        empty={tr('ipd', 'sp_nothing_booked')}
        columns={[
          { header: tr('ipd', 'sp_time'), sort: (row) => row.scheduledAt, cell: (row) => timeOnly(row.scheduledAt) },
          { header: tr('ipd', 'sp_session'), sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
          {
            header: tr('ipd', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
              </div>
            ),
          },
          {
            header: tr('ipd', 'sp_station'),
            sort: (row) => row.station?.name,
            cell: (row) => row.station?.name ?? <span className="text-muted-foreground">{tr('ipd', 'sp_unassigned')}</span>,
          },
          { header: tr('ipd', 'sp_cycle'), align: 'right', sort: (row) => row.cycleNo, cell: (row) => row.cycleNo },
          { header: tr('ipd', 'sp_protocol'), sort: (row) => row.protocol, cell: (row) => row.protocol ?? '—' },
          { header: tr('ipd', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {row.status === 'scheduled' && (
                  <Button size="sm" onClick={() => start.mutate(row)} loading={start.isPending}>
                    {tr('ipd', 'sp_start')}
                  </Button>
                )}
                {row.status === 'in_progress' && (
                  <Button size="sm" onClick={() => setFinishing(row)}>
                    {tr('ipd', 'sp_complete')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {finishing !== null && (
        <CompleteSessionDialog session={finishing} onClose={() => setFinishing(null)} />
      )}
    </>
  );
}


function CompleteSessionDialog({ session, onClose }: { session: Session | null; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [postWeight, setPostWeight] = useState('');
  const [complications, setComplications] = useState('');
  const [error, setError] = useState<string | null>(null);

  const complete = useMutation({
    mutationFn: () =>
      post(`/therapy/sessions/${session?.id}/complete`, {
        postWeightKg: postWeight ? Number(postWeight) : undefined,
        complications: complications || undefined,
        consumables: [],
        bill: true,
      }),
    onSuccess: () => {
      toast.success(tr('ipd', 'sp_session_completed'), tr('ipd', 'sp_charge_on_bill'));
      void queryClient.invalidateQueries({ queryKey: ['therapy-sessions'] });
      setPostWeight('');
      setComplications('');
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open={Boolean(session)}
      onClose={onClose}
      title={tr('ipd', 'sp_complete_session')}
      description={session ? `${session.patient.fullName} · ${session.number}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              complete.mutate();
            }}
            loading={complete.isPending}
          >
            {tr('ipd', 'sp_complete')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {session?.preWeightKg && (
          <Alert>
            {tr('ipd', 'sp_pre_session_weight', { weight: session.preWeightKg })}
            {postWeight && ` · ${tr('ipd', 'sp_weight_removed', { weight: (session.preWeightKg - Number(postWeight)).toFixed(1) })}`}
          </Alert>
        )}
        <Field label={tr('ipd', 'sp_post_session_weight')}>
          <Input type="number" step="0.1" value={postWeight} onChange={(e) => setPostWeight(e.target.value)} />
        </Field>
        <Field label={tr('ipd', 'sp_complications')}>
          <Textarea rows={2} value={complications} onChange={(e) => setComplications(e.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

// ── Dental (SRS 6.8) ─────────────────────────────────────────────────────────

/** FDI quadrants, laid out as the dentist sees the mouth. */
const QUADRANTS: [string, string[]][] = [
  ['sp_upper_right', ['18', '17', '16', '15', '14', '13', '12', '11']],
  ['sp_upper_left', ['21', '22', '23', '24', '25', '26', '27', '28']],
  ['sp_lower_right', ['48', '47', '46', '45', '44', '43', '42', '41']],
  ['sp_lower_left', ['31', '32', '33', '34', '35', '36', '37', '38']],
];

export function DentalPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [patientId, setPatientId] = useState(() => params.get('patientId') ?? '');

  const chart = useQuery({
    queryKey: ['tooth-chart', patientId],
    queryFn: () => get<ToothChartEntry[]>(`/dental/patients/${patientId}/chart`),
    enabled: Boolean(patientId),
  });

  const byTooth = new Map<string, ToothChartEntry>();
  for (const entry of chart.data ?? []) {
    if (!byTooth.has(entry.toothNo)) byTooth.set(entry.toothNo, entry);
  }

  return (
    <>
      <PageHeader title={tr('ipd', 'sp_dental')} description={tr('ipd', 'sp_dental_description')} />

      <FilterBar>
        <FilterField label={tr('ipd', 'patient')} className="w-full max-w-md">
          <PatientPicker value={patientId} onChange={setPatientId} />
        </FilterField>
      </FilterBar>

      {patientId && (
        <>
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-sm">{tr('ipd', 'sp_tooth_chart')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {QUADRANTS.map(([label, teeth]) => (
                <div key={label}>
                  <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">{tr('ipd', label)}</p>
                  <div className="flex flex-wrap gap-1">
                    {teeth.map((toothNo) => {
                      const entry = byTooth.get(toothNo);
                      return (
                        <button
                          key={toothNo}
                          type="button"
                          onClick={() => navigate(`/dental/treatments/new?patientId=${patientId}&toothNo=${toothNo}`)}
                          className={cn(
                            'h-11 w-11 rounded-md border-2 text-sm font-medium focus-ring',
                            !entry && 'border-border hover:bg-muted',
                            entry?.status === 'completed' && 'border-success/50 bg-success/10 text-success',
                            entry?.status === 'planned' && 'border-warning/50 bg-warning/10 text-warning',
                            entry?.status === 'in_progress' && 'border-primary/50 bg-primary/10 text-primary',
                          )}
                          title={entry ? `${entry.finding}${entry.procedure ? ` · ${entry.procedure}` : ''}` : tr('ipd', 'sp_healthy')}
                        >
                          {toothNo}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <DataTable
            rows={chart.data}
            loading={chart.isLoading} refreshing={chart.isFetching}
            error={chart.error}
            rowKey={(row) => `${row.toothNo}-${row.plannedAt}`}
            empty={tr('ipd', 'sp_nothing_charted')}
            columns={[
              { header: tr('ipd', 'sp_tooth'), sort: (row) => row.toothNo, cell: (row) => <span className="font-mono">{row.toothNo}</span> },
              { header: tr('ipd', 'sp_finding'), sort: (row) => row.finding, cell: (row) => row.finding },
              { header: tr('ipd', 'sp_procedure'), sort: (row) => row.procedure, cell: (row) => row.procedure ?? '—' },
              { header: tr('ipd', 'sp_surfaces'), cell: (row) => row.surfaces.map((surface) => humanize(surface)).join(', ') || '—' },
              { header: tr('ipd', 'sp_planned'), sort: (row) => row.plannedAt, cell: (row) => dateOnly(row.plannedAt) },
              { header: tr('ipd', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
            ]}
          />
        </>
      )}

    </>
  );
}

